/**
 * «Online сейчас» — кто прямо сейчас подключён к VPN.
 *
 * Источник: GET /admin/online (DISTINCT ON подписка из connection_logs за
 * последние 5 минут — auth-callback от Hysteria-нод). Одна строка = одна
 * подписка. Авто-refresh каждые 15 сек (live-картина).
 *
 * Реализация на прямом fetch + setInterval (а не useCustom): в этой сборке
 * Refine v5+antd@6 хук useCustom не отдаёт стабильно refetch/isFetching на
 * верхнем уровне — поэтому управляем загрузкой сами (так же как DomainStats).
 *
 * ВАЖНО (ограничение источника): online виден только для нод на auth.http
 * (Vienna, Helsinki, Amsterdam). Ноды на static-пароле callback не шлют.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
  Card, Table, Tag, Space, Typography, Button, Statistic, Row, Col, Tooltip,
  App as AntApp,
} from "antd";
import { ReloadOutlined, GlobalOutlined, WifiOutlined } from "@ant-design/icons";
import { Link } from "react-router";
import { API_URL } from "../../dataProvider";
import { TARIFF_LABEL, TARIFF_COLOR, type OnlineItem } from "../../types";
import { countryFlag, fmtAgo, fmtDateTime, daysLeft } from "../../utils";

const REFRESH_MS = 15_000;

export const OnlinePage = () => {
  const { message } = AntApp.useApp();
  const [rows, setRows] = useState<OnlineItem[]>([]);
  const [loading, setLoading] = useState(true); // первый показ — спиннер
  const [fetching, setFetching] = useState(false); // фоновые refresh
  const firstLoad = useRef(true);

  const load = useCallback(async () => {
    if (firstLoad.current) setLoading(true);
    else setFetching(true);
    try {
      const r = await fetch(`${API_URL}/admin/online`, { credentials: "include" });
      if (!r.ok) {
        const t = await r.text().catch(() => r.statusText);
        message.error(`Не удалось загрузить online: ${r.status} ${t.slice(0, 150)}`);
        return;
      }
      const body: OnlineItem[] = await r.json();
      setRows(Array.isArray(body) ? body : []);
    } catch (e) {
      message.error(`Сетевая ошибка: ${(e as Error).message}`);
    } finally {
      setLoading(false);
      setFetching(false);
      firstLoad.current = false;
    }
  }, [message]);

  useEffect(() => {
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => clearInterval(t);
  }, [load]);

  // Группировка по локации ноды — для KPI «по странам».
  const byNode = rows.reduce<Record<string, number>>((acc, r) => {
    const key = r.node_location || "—";
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={8} md={6}>
          <Card size="small">
            <Statistic
              title="Сейчас online"
              value={rows.length}
              valueStyle={{ color: rows.length > 0 ? "#52c41a" : undefined }}
              prefix={<WifiOutlined />}
              suffix={<Typography.Text type="secondary"> подп.</Typography.Text>}
            />
          </Card>
        </Col>
        <Col xs={24} sm={16} md={18}>
          <Card size="small">
            <Space size="middle" wrap split={<Typography.Text type="secondary">·</Typography.Text>}>
              <Typography.Text type="secondary">По нодам:</Typography.Text>
              {Object.keys(byNode).length === 0 ? (
                <Typography.Text type="secondary">никого нет онлайн</Typography.Text>
              ) : (
                Object.entries(byNode)
                  .sort((a, b) => b[1] - a[1])
                  .map(([loc, n]) => (
                    <Tag key={loc} color="green">
                      {countryFlag(loc)}: {n}
                    </Tag>
                  ))
              )}
            </Space>
          </Card>
        </Col>
      </Row>

      <Card
        title={
          <Space>
            <span>Кто сейчас онлайн</span>
            <Tag color={fetching ? "processing" : "default"}>
              {fetching ? "обновляется…" : "live · каждые 15 сек"}
            </Tag>
          </Space>
        }
        extra={
          <Button
            size="small"
            icon={<ReloadOutlined spin={fetching} />}
            onClick={load}
          >
            Обновить
          </Button>
        }
      >
        <Table<OnlineItem>
          loading={loading}
          rowKey="subscription_id"
          dataSource={rows}
          size="small"
          pagination={{ pageSize: 25, hideOnSinglePage: true }}
          locale={{
            emptyText:
              "Никто не подключён за последние 5 минут (или ноды на static-пароле).",
          }}
          columns={[
            {
              title: "Юзер",
              key: "user",
              render: (_, r) => (
                <Space direction="vertical" size={0}>
                  <Link to={`/users/${r.user_id}`}>
                    <Typography.Text strong>
                      {r.tg_username
                        ? `@${r.tg_username}`
                        : r.first_name || `tg:${r.user_id}`}
                    </Typography.Text>
                  </Link>
                  {r.label && (
                    <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                      {r.label}
                    </Typography.Text>
                  )}
                </Space>
              ),
            },
            {
              title: "UUID",
              dataIndex: "subscription_id",
              render: (v: number) => (
                <Link to={`/subscriptions/${v}`}>
                  <Typography.Text code>#{v}</Typography.Text>
                </Link>
              ),
            },
            {
              title: "Тариф",
              dataIndex: "tariff",
              render: (v: OnlineItem["tariff"]) => (
                <Tag color={TARIFF_COLOR[v]}>{TARIFF_LABEL[v] ?? v}</Tag>
              ),
            },
            {
              title: "Нода",
              key: "node",
              render: (_, r) => (
                <Space size={4}>
                  <GlobalOutlined />
                  <span>{countryFlag(r.node_location)}</span>
                </Space>
              ),
            },
            {
              title: "Гео клиента",
              key: "geo",
              render: (_, r) =>
                r.country || r.city ? (
                  <Typography.Text>
                    {[r.city, r.country].filter(Boolean).join(", ")}
                  </Typography.Text>
                ) : (
                  <Typography.Text type="secondary">—</Typography.Text>
                ),
            },
            {
              title: "Активность",
              key: "auths",
              align: "right",
              render: (_, r) => (
                <Tooltip title="Сколько auth-callback'ов пришло от этой подписки за 5 мин (≈ число переподключений/устройств).">
                  <Tag color={r.auths_5min > 10 ? "orange" : "blue"}>
                    {r.auths_5min}
                  </Tag>
                </Tooltip>
              ),
            },
            {
              title: "Последний раз",
              dataIndex: "last_seen",
              render: (v: string | null) => (
                <Tooltip title={fmtDateTime(v)}>
                  <Tag color="green">{fmtAgo(v)}</Tag>
                </Tooltip>
              ),
            },
            {
              title: "Подписка до",
              dataIndex: "end_at",
              render: (v: string | null) => {
                const left = daysLeft(v);
                return (
                  <Space size={4}>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {fmtDateTime(v)}
                    </Typography.Text>
                    {left !== null && (
                      <Tag
                        color={
                          left <= 0 ? "red" : left <= 3 ? "orange" : left <= 7 ? "gold" : "default"
                        }
                      >
                        {left > 0 ? `${left} д.` : "истекла"}
                      </Tag>
                    )}
                  </Space>
                );
              },
            },
          ]}
        />
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Источник: auth-callback Hysteria-нод за 5 минут. IP-адрес клиента
          намеренно не показываем. Ноды на static-пароле в этом списке не видны.
        </Typography.Text>
      </Card>
    </Space>
  );
};
